import React, { useEffect, useState } from 'react';
import { Modal, Box, Button, Typography } from '@mui/material';

import { Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import type {
    StudentMasteryByStandardDTO,
    StudentMasteryByDomainDTO,
} from '../models/studentStandardsReportDTOs';
import StudentStandardMasteryTable from './StudentStandardMasteryTable';
import StudentDomainMasteryTable from './StudentDomainMasteryTable';

interface StudentMasteryModalProps {
    open: boolean;
    onClose: () => void;
    studentId: number;
    studentName: string;
    standards: Array<{ standardId: number; standardCode: string }>;
}

// Per-student mastery drilldown — opened from the Student Standards Report
// row link. Owns its own fetch + loading/error state so the parent only needs
// to control visibility + the student/standards context.
const StudentMasteryModal: React.FC<StudentMasteryModalProps> = ({
    open,
    onClose,
    studentId,
    studentName,
    standards,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [standardRows, setStandardRows] = useState<StudentMasteryByStandardDTO[]>([]);
    const [domainRows, setDomainRows] = useState<StudentMasteryByDomainDTO[]>([]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        async function fetchData() {
            if (!studentId || Number.isNaN(studentId)) {
                if (!cancelled) {
                    setError('Invalid student id.');
                    setLoading(false);
                }
                return;
            }
            if (standards.length === 0) {
                if (!cancelled) {
                    setError(
                        'No data to display. Return to the Student Standards Report to try again.'
                    );
                    setLoading(false);
                }
                return;
            }

            setError('');
            setLoading(true);
            setStandardRows([]);
            setDomainRows([]);

            try {
                const perStandard = await Promise.all(
                    standards.map(async (s) => {
                        const { data } = await apiRequest<StudentMasteryByStandardDTO>(
                            'GET',
                            `reports/classStandardsReport/mastery/student/standard?studentId=${studentId}&standardId=${s.standardId}`
                        );
                        return data;
                    })
                );

                const domainIds = [
                    ...new Set(perStandard.map((m) => m.standardDomainMasterId).filter(Boolean)),
                ];
                const perDomain = await Promise.all(
                    domainIds.map(async (domId) => {
                        const { data } = await apiRequest<StudentMasteryByDomainDTO>(
                            'GET',
                            `reports/classStandardsReport/mastery/student/domain?studentId=${studentId}&standardDomainMasterId=${domId}`
                        );
                        return data;
                    })
                );

                perStandard.sort((a, b) => a.standardCode.localeCompare(b.standardCode));
                perDomain.sort((a, b) =>
                    (a.domainShortName ?? '').localeCompare(b.domainShortName ?? '')
                );

                if (!cancelled) {
                    setStandardRows(perStandard);
                    setDomainRows(perDomain);
                }
            } catch {
                if (!cancelled) setError('Failed to load Student Standards Report.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        fetchData();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, studentId]);

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    width: '85%',
                    height: '85%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Typography variant="h6" fontWeight="bold" mb={2}>
                    Student Standards Report{studentName ? ` — ${studentName}` : ''}
                </Typography>

                <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                    <Loading visible={loading} message="Loading Student Standards Report..." />
                    <ErrorMessage
                        error={error}
                        style={{ textAlign: 'center', marginBottom: '8px' }}
                    />

                    {!loading && !error && (
                        <>
                            <StudentStandardMasteryTable data={standardRows} />
                            <StudentDomainMasteryTable data={domainRows} />
                        </>
                    )}
                </Box>

                <Box textAlign="right" mt={2}>
                    <Button variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default StudentMasteryModal;
